import {
  AlertCircle,
  CheckCircle,
  FileText,
  Globe,
  PenLine,
  Sparkles,
  Upload,
} from "lucide-react";
import React, { useState } from "react";
import { useTranslation } from "../../contexts/LanguageContext";
import { cn } from "../../utils/cn";
import { getPlatformConfig, PLATFORMS_LIST } from "../../utils/platformIcons";
import { ContentEntry } from "../cards/ContentCard";
import Button from "../ui/Button";
import CharCounter from "../ui/CharCounter";
import Input from "../ui/Input";
import Modal from "../ui/Modal";
import Select from "../ui/Select";
import Textarea from "../ui/Textarea";

interface EditContentModalProps {
  open: boolean;
  onClose: () => void;
  content: ContentEntry | null;
  onSave: (data: Partial<ContentEntry>) => Promise<void>;
  loading?: boolean;
}

/* ── Plataformas: mesma fonte de ícones dos outros modais (evita ícones
   e comportamento de cor divergentes) ── */
const CONTENT_PLATFORM_IDS = [
  "linkedin",
  "facebook",
  "instagram",
  "google_my_business",
];
const platformOptions = PLATFORMS_LIST.filter((p) =>
  CONTENT_PLATFORM_IDS.includes(p.id),
).map((p) => ({
  id: p.id,
  name: p.id === "google_my_business" ? "Google" : p.name,
  icon: <p.icon className="h-4 w-4" />,
}));

/* ── Tipos ── */
const contentTypesMeta = [
  {
    id: "url" as const,
    label: "URL / Site",
    icon: <Globe className="h-4 w-4" />,
    iconColor: "text-blue-600",
    iconBg: "bg-blue-50",
    iconBorder: "border-blue-200",
  },
  {
    id: "text" as const,
    label: "Texto",
    icon: <FileText className="h-4 w-4" />,
    iconColor: "text-emerald-600",
    iconBg: "bg-emerald-50",
    iconBorder: "border-emerald-200",
  },
  {
    id: "prompt" as const,
    label: "Prompt IA",
    icon: <Sparkles className="h-4 w-4" />,
    iconColor: "text-purple-600",
    iconBg: "bg-purple-50",
    iconBorder: "border-purple-200",
  },
  {
    id: "file" as const,
    label: "Arquivo",
    icon: <Upload className="h-4 w-4" />,
    iconColor: "text-orange-600",
    iconBg: "bg-orange-50",
    iconBorder: "border-orange-200",
  },
];

interface FormState {
  name: string;
  type: "url" | "text" | "prompt" | "file";
  original_data: string;
  platforms: string[];
  business_type: string;
}

const MAX_CHARS: Record<string, number> = {
  url: 500,
  text: 5000,
  prompt: 2000,
  file: 0,
};

const EditContentModal: React.FC<EditContentModalProps> = ({
  open,
  onClose,
  content,
  onSave,
  loading,
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(() =>
    content
      ? {
          name: content.name,
          type: content.type,
          original_data: content.original_data,
          platforms: content.platforms,
          business_type: content.business_type,
        }
      : {
          name: "",
          type: "text",
          original_data: "",
          platforms: [],
          business_type: "b2b",
        },
  );
  const [error, setError] = useState<string | null>(null);
  // Vira `true` na primeira tentativa de envio com campos obrigatórios
  // vazios — liga o destaque visual (borda vermelha) nesses campos.
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  React.useEffect(() => {
    if (content) {
      setForm({
        name: content.name,
        type: content.type,
        original_data: content.original_data,
        platforms: content.platforms,
        business_type: content.business_type,
      });
      setError(null);
      setAttemptedSubmit(false);
    }
  }, [content]);

  const handleChange = (field: keyof FormState, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePlatformToggle = (platformId: string) => {
    setForm((prev) => ({
      ...prev,
      platforms: prev.platforms.includes(platformId)
        ? prev.platforms.filter((p) => p !== platformId)
        : [...prev.platforms, platformId],
    }));
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError(null);
    setAttemptedSubmit(true);
    if (!form.name.trim() || !form.original_data.trim()) {
      setError(t("Nome e dados do conteúdo são obrigatórios"));
      return;
    }
    if (form.platforms.length === 0) {
      setError(t("Selecione pelo menos uma plataforma"));
      return;
    }
    await onSave(form);
  };

  if (!content) return null;

  const maxChars = MAX_CHARS[form.type] || 0;
  const charCount = form.original_data.length;

  const originalTypeMeta = contentTypesMeta.find((t) => t.id === content.type);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("Editar Conteúdo")}
      subtitle={t("Fazendo ajustes em: {name}", { name: content.name })}
      icon={<PenLine className="h-5 w-5" />}
      headerVariant="gradient"
      maxWidth="max-w-3xl"
      footer={
        <div className="flex justify-end gap-3 w-full">
          <Button
            type="button"
            onClick={onClose}
            size="sm"
            variant="secondary"
            disabled={loading}
          >
            {t("Cancelar")}
          </Button>
          <Button
            type="submit"
            form="edit-content-form"
            size="sm"
            variant="primary"
            disabled={
              loading ||
              !form.name.trim() ||
              !form.original_data.trim() ||
              form.platforms.length === 0
            }
            isLoading={loading}
            className="shadow-lg shadow-purple-500/20 px-6"
          >
            {loading ? t("Salvando...") : t("Salvar Alterações")}
          </Button>
        </div>
      }
    >
      <form
        id="edit-content-form"
        onSubmit={handleSubmit}
        className="p-0.5 space-y-5"
      >
        {/* Contexto Rápido */}
        {originalTypeMeta && (
          <div className="flex items-center justify-between p-3.5 bg-gray-50/80 rounded-2xl border border-purple-200 shadow-inner">
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  "p-2 rounded-xl border",
                  originalTypeMeta.iconBg,
                  originalTypeMeta.iconBorder,
                )}
              >
                {React.cloneElement(
                  originalTypeMeta.icon as React.ReactElement<any>,
                  { className: cn("h-4 w-4", originalTypeMeta.iconColor) },
                )}
              </div>
              <div>
                <h4 className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                  {t("Origem do Registro")}
                </h4>
                <p className="text-xs font-bold text-gray-700">
                  {t(originalTypeMeta.label)}
                </p>
              </div>
            </div>
            <div className="text-right">
              <h4 className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                {t("Cadastrado em")}
              </h4>
              <p className="text-xs font-bold text-gray-700">
                {new Date(content.created_at).toLocaleDateString("pt-BR", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </p>
            </div>
          </div>
        )}

        {/* Erro */}
        {error && (
          <div className="flex items-start gap-2.5 p-3.5 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm animate-shake">
            <svg
              className="h-4 w-4 shrink-0 mt-0.5 text-red-500"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <span className="font-medium">{error}</span>
          </div>
        )}

        {/* Nome */}
        <div className="space-y-2">
          <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400 px-1">
            {t("Nome do Conteúdo")} <span className="text-red-400">*</span>
          </label>
          <Input
            type="text"
            value={form.name}
            onChange={(e) => handleChange("name", e.target.value)}
            placeholder={t("Nome do conteúdo...")}
            required
            error={
              attemptedSubmit && !form.name.trim()
                ? t("O nome do conteúdo é obrigatório")
                : undefined
            }
            inputSize="sm"
            className="bg-gray-50! focus:bg-white! transition-all shadow-inner"
          />
        </div>

        {/* Tipo de Formato */}
        <div className="space-y-3">
          <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400 px-1">
            {t("Alterar Categoria")}
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {contentTypesMeta.map((type) => {
              const isActive = form.type === type.id;
              return (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => handleChange("type", type.id)}
                  className={cn(
                    "flex items-center gap-2.5 px-3 py-2 rounded-xl border-2 transition-all duration-300 text-left",
                    "focus:outline-hidden focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-1",
                    isActive
                      ? "bg-purple-600 border-transparent text-white shadow-lg scale-[1.02]"
                      : "bg-gray-50 border-gray-200 text-gray-700 hover:border-purple-300 hover:bg-purple-50/50",
                  )}
                >
                  <div
                    className={cn(
                      "p-1.5 rounded-lg border transition-colors shrink-0",
                      isActive
                        ? "bg-white/20 border-white/20"
                        : `${type.iconBg} ${type.iconBorder}`,
                    )}
                  >
                    {React.cloneElement(type.icon as React.ReactElement<any>, {
                      className: cn(
                        "h-4 w-4",
                        isActive ? "text-white" : type.iconColor,
                      ),
                    })}
                  </div>
                  <span className="text-xs font-bold uppercase tracking-tight truncate">
                    {t(type.label)}
                  </span>
                  {isActive && (
                    <CheckCircle className="h-3.5 w-3.5 text-white/80 shrink-0 ml-auto" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Bloco de Dados */}
        <div className="space-y-2">
          <div className="flex items-center justify-between px-1">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400">
              {t("Dados do Registro")} <span className="text-red-400">*</span>
            </label>
            {maxChars > 0 && (
              <CharCounter current={charCount} hard={maxChars} />
            )}
          </div>
          <Textarea
            value={form.original_data}
            onChange={(e) => handleChange("original_data", e.target.value)}
            maxLength={maxChars || undefined}
            error={
              attemptedSubmit && !form.original_data.trim()
                ? t("O conteúdo é obrigatório")
                : undefined
            }
            className={cn(
              "p-4 rounded-2xl! focus:border-purple-500! text-sm bg-gray-50! focus:bg-white! placeholder:text-gray-400! leading-relaxed shadow-inner",
              attemptedSubmit && !form.original_data.trim()
                ? "border-red-300!"
                : "border-purple-200!",
              form.type === "text" || form.type === "prompt"
                ? "min-h-[140px]!"
                : "min-h-[80px]!",
            )}
            required
          />
        </div>

        {/* Plataformas Destino */}
        <div className="space-y-3">
          <div className="flex items-center justify-between px-1">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400">
              {t("Canais de Distribuição")}{" "}
              <span className="text-red-400">*</span>
            </label>
            <span className="text-[10px] font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full border border-purple-200">
              {t("{count} Selecionados", { count: form.platforms.length })}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {platformOptions.map((platform) => {
              const isActive = form.platforms.includes(platform.id);
              const platformConfig = getPlatformConfig(platform.id);
              return (
                <button
                  key={platform.id}
                  type="button"
                  onClick={() => handlePlatformToggle(platform.id)}
                  className={cn(
                    "flex items-center justify-center gap-2 px-3 py-2 rounded-xl border-2 transition-all duration-300 group",
                    "focus:outline-hidden focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-1",
                    isActive
                      ? "bg-purple-600 border-transparent text-white shadow-lg scale-[1.02]"
                      : attemptedSubmit && form.platforms.length === 0
                        ? "bg-gray-50 border-red-300 text-gray-700 hover:border-red-400 hover:bg-red-50/50"
                        : "bg-gray-50 border-gray-200 text-gray-700 hover:border-purple-300 hover:bg-purple-50/50",
                  )}
                >
                  <div
                    className={cn(
                      "p-1.5 rounded-lg border transition-colors",
                      isActive
                        ? "bg-white/20 border-white/20"
                        : `${platformConfig.bgColor} ${platformConfig.borderColor}`,
                    )}
                  >
                    {React.cloneElement(
                      platform.icon as React.ReactElement<any>,
                      {
                        className: cn(
                          "h-4 w-4",
                          isActive ? "text-white" : platformConfig.color,
                        ),
                      },
                    )}
                  </div>
                  <span className="text-xs font-bold uppercase tracking-tight whitespace-nowrap">
                    {platform.name}
                  </span>
                  {isActive && (
                    <CheckCircle className="h-3.5 w-3.5 text-white/80" />
                  )}
                </button>
              );
            })}
          </div>
          {attemptedSubmit && form.platforms.length === 0 && (
            <p className="flex items-center gap-1 text-xs text-red-600 font-medium px-1">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              {t("Selecione pelo menos uma plataforma")}
            </p>
          )}
        </div>

        {/* Segmento */}
        <div className="space-y-2">
          <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400 px-1">
            {t("Público do Conteúdo")}
          </label>
          <Select
            options={[
              {
                value: "b2b",
                label: t("B2B (Enterprise / Pessoas de Negócios)"),
              },
              { value: "b2c", label: t("B2C (Consumidor / Varejo)") },
            ]}
            value={form.business_type}
            onChange={(v) => handleChange("business_type", v)}
            size="sm"
            className="border-purple-200!"
          />
        </div>
      </form>
    </Modal>
  );
};

export default EditContentModal;
